// The weekly recap on a simulated league (RECAP.md): the pure engine from
// @fsp/recap over the same season facts the awards use. The sim writes the
// recap when it mints a period's awards, posts its card right after the
// awards card, serves the Recap screen, and gives Home its "your week in
// one sentence".

import type { ChatMessage, HomeView, RecapView } from '@fsp/domain';
import type { SeasonFacts } from '@fsp/awards';
import { personalLine, weeklyRecap, type NextPeriod } from '@fsp/recap';
import { cardOf, seasonFacts } from './awards';
import { systemMessage } from './chat';
import type { SimState } from './league';
import { leagueRef, periodRef, periodStatus, teamRef } from './views';

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

/** Next period's pairings from the schedule, and whether the rules call it a rivalry week. */
export function nextPeriodInput(state: SimState, period: number): NextPeriod | null {
  const next = period + 1;
  const ms = state.schedule.filter((m) => m.period === next);
  if (!ms.length) return null;
  return {
    period: next,
    matchups: ms.map((m) => ({ homeTeamId: m.home, awayTeamId: m.away })),
    rivalry: state.settings.schedule.rivalryPeriods.includes(next),
  };
}

function recapFor(state: SimState, facts: SeasonFacts, period: number) {
  return weeklyRecap({
    facts,
    period,
    minted: state.awards.minted,
    next: nextPeriodInput(state, period),
    playoffTeamCount: state.settings.playoffs.teamCount,
  });
}

/** The Recap screen: one period's recap, the latest final period by default. */
export function recapView(state: SimState, now: number, period?: number): RecapView {
  const facts = seasonFacts(state, now);
  const refs = new Map(state.teams.map((t) => [t.id, teamRef(state, t, now)]));
  const current = state.currentPeriod;
  const status = state.status === 'in-season' ? periodStatus(state, current, now) : 'pre';
  const periods = facts.periods.filter((p) => p.final).map((p) => p.period);
  const selected = period ?? periods[periods.length - 1] ?? current;
  const recap = recapFor(state, facts, selected);
  const mine = state.teams.find((t) => t.isMine) ?? null;
  const awards = state.awards.minted
    .filter((m) => m.scope === 'period' && m.period === selected && !m.supersededBy)
    .map((m) => cardOf(m, refs));
  return {
    league: leagueRef(state),
    period: periodRef(state, selected),
    status: selected === current ? status : 'final',
    periods,
    headline: recap?.headline ?? null,
    standfirst: recap?.standfirst ?? null,
    sections: recap
      ? recap.sections.map((s) => ({
          id: s.id,
          title: s.title,
          lines: s.lines.map((l) => ({
            text: l.text,
            team: l.teamId ? (refs.get(l.teamId) ?? null) : null,
            matchupId: l.matchupId,
            names: l.names,
            emphasis: l.emphasis,
          })),
        }))
      : [],
    awards,
    mine:
      mine && recap
        ? personalLine(facts, selected, mine.id, state.settings.playoffs.teamCount)
        : null,
    at: awards[0]?.at ?? null,
    updatedAt: iso(now),
  };
}

/** The chat card for a period's recap; null when the period has no recap. */
export function recapMessage(
  state: SimState,
  facts: SeasonFacts,
  period: number,
  at: number,
): ChatMessage | null {
  const recap = recapFor(state, facts, period);
  if (!recap) return null;
  const games =
    recap.sections
      .find((s) => s.id === 'games')
      ?.lines.slice(0, 3)
      .map((l) => l.text) ?? [];
  return systemMessage(state, at, `Week ${period} recap: ${recap.headline} ${recap.standfirst}`, {
    kind: 'recap',
    period,
    headline: recap.headline,
    standfirst: recap.standfirst,
    lines: games,
  });
}

// The line does not change between ticks while a period is final or has
// not started, and Home asks for it every second; cache it per state.
const weekCache = new WeakMap<SimState, { key: string; value: HomeView['myWeek'] }>();

/** Home's "your week in one sentence": the latest final period, shown until the next kickoff. */
export function myWeek(state: SimState, now: number): HomeView['myWeek'] {
  if (state.status !== 'in-season') return null;
  const status = periodStatus(state, state.currentPeriod, now);
  if (status === 'live') return null;
  const period = status === 'final' ? state.currentPeriod : state.currentPeriod - 1;
  if (period < 1 || !state.schedule.some((m) => m.period === period)) return null;
  const mine = state.teams.find((t) => t.isMine);
  if (!mine) return null;
  const key = `${period}:${status}:${mine.id}`;
  const cached = weekCache.get(state);
  if (cached && cached.key === key) return cached.value;
  const facts = seasonFacts(state, now);
  const line = personalLine(facts, period, mine.id, state.settings.playoffs.teamCount);
  const m = state.schedule.find(
    (x) => x.period === period && (x.home === mine.id || x.away === mine.id),
  );
  const value = line ? { period, line, matchupId: m?.id ?? null } : null;
  weekCache.set(state, { key, value });
  return value;
}
